import React, { useState } from 'react';
import './ElectronicSignaturePage.css';
import countryImage from '../../../../assets/Images/country.jpg'
import InteractiveEsignMap from '../InteractiveEsignMap';

const ElectronicSignaturePage = () => {
  const [selectedCountry, setSelectedCountry] = useState('Equatorial Guinea');
  const [expandedFAQ, setExpandedFAQ] = useState([0, 3]);

  const countries = [
    'Equatorial Guinea',
    'United States',
    'Canada',
    'United Kingdom',
    'China',
    'India',
    'Germany',
    'Australia',
    'France',
    'Japan'
  ];

  const countryData = {
    'Equatorial Guinea': { holdsInCourt: false, business: false },
    'United States': { holdsInCourt: true, business: true },
    'Canada': { holdsInCourt: true, business: true },
    'United Kingdom': { holdsInCourt: true, business: true },
    'China': { holdsInCourt: true, business: true },
    'India': { holdsInCourt: true, business: true },
    'Germany': { holdsInCourt: true, business: true },
    'Australia': { holdsInCourt: true, business: true },
    'France': { holdsInCourt: true, business: true },
    'Japan': { holdsInCourt: true, business: true }
  };

  const legalCountries = [
    { code: 'us', title: 'United States', description: 'Uniform Electronic Transactions Act (UETA) – Electronic Signatures in Global and National Commerce Act' },
    { code: 'ca', title: 'Canada', description: 'Personal Information Protection and Electronic Documents Act (PIPEDA)' },
    { code: 'gb', title: 'United Kingdom', description: 'The Electronic Identification Regulation (EU/910/2014) (2014)' },
    { code: 'cn', title: 'China', description: "Electronic Signature Law of the People's Republic of China" },
    { code: 'in', title: 'India', description: 'Information Technology Act, 2000' },
    { code: 'de', title: 'Germany', description: 'eIDAS (Electronic Identification, Authentication and Trust Services)' },
    { code: 'fr', title: 'France', description: 'eIDAS (Electronic Identification, Authentication and Trust Services)' },
    { code: 'es', title: 'Spain', description: 'eIDAS (Electronic Identification, Authentication and Trust Services)' },
    { code: 'it', title: 'Italy', description: 'eIDAS (Electronic Identification, Authentication and Trust Services)' },
    { code: 'ru', title: 'Russian Federation', description: 'Federal Law of Russian Federation about Electronic Signature (06.04.2011)' },
    { code: 'jp', title: 'Japan', description: 'Law Concerning Electronic Signatures and Certification Services, 2000' },
    { code: 'kr', title: 'South Korea', description: 'Digital Signature Act' },
    { code: 'tr', title: 'Turkey', description: 'Electronic Signature Law' },
    { code: 'ch', title: 'Switzerland', description: 'Federal Law on Certification Services Concerning the Electronic Signature, 2003' },
    { code: 'br', title: 'Brazil', description: 'Medida Provisória: 2.200-2' }
  ];



  const faqs = [
    {
      question: 'Are eSignatures legally binding?',
      answer:
        'Yes, they are. eSignatures are legally binding virtually everywhere in the world and across different industries. They hold up in court and can be used as evidence. Each eSignature records digital stamps (like an IP address) that make it easy to track back to each signee. Learn more about eSignatures here.'
    },
    {
      question: 'Can I sign any kind of document with eSignatures?',
      answer:
        'Yes. As stated in the Electronic Signatures in Global and National Commerce Act (ESIGN – USA), a contract or signature “may not be denied legal effect, validity, or enforceability solely because it is in electronic form”. eSignatures are widely accepted across industries and uses. Find templates for your business here.'
    },
    {
      question: 'Can I sign on a PDF with eSignatures?',
      answer:
        'Yes, you can sign on a PDF. Signaturely also supports PNG and JPG files, and Word documents. You can integrate with Google Drive, Dropbox, Box and OneDrive to upload your documents.'
    },
    {
      question: "I've never used an eSignature before. Is it the same as a manual signature?",
      answer:
        'It is exactly the same. The only difference is you can sign remotely from anywhere in the world since you don’t need to print documents to sign them. When signing, Signaturely will ask you to enter your signature by drawing it, typing your name or uploading a photo.'
    },
    {
      question: 'Is the audit trail of eSignatures as legally binding as paper archives?',
      answer:
        'Absolutely. Signaturely saves digital imprints for every interaction you and other signees have with your documents. This makes documents easy to track and recover at any point, just by signing into your account, instead of archiving stacks of paper files. eSigned documents have been confidently used as evidence in court for years.'
    },
    {
      question: 'Do I need to keep a paper record for my eSigned documents?',
      answer:
        'No need. You can save, track, and come back to your documents in your Signaturely dashboard. Digital records are as valid as paper records for audits, legal processes, and compliance issues.'
    },
    {
      question: 'Some people still prefer to sign on paper. What should I do?',
      answer:
        'Dealing with clients or partners who distrust eSignatures is an opportunity for growth. We recommend pointing them to the relevant eSign legislation where they do business. Once people realize how much time and money they save with eSignatures, they’ll be grateful you took the time to educate them.'
    },
    {
      question: 'What are cases in which eSignatures definitely don’t work?',
      answer:
        'eSignatures make the signing process easier and faster for most cases. However, when it comes to documents that need an extra layer of identification, notarization, or one or more witnesses, eSignatures might not be sufficient. Documents with a greater risk of fraud due to a false or forged signature include: Wills, Sworn declarations, Powers of attorney, and Major financial transactions that require a notarized document.'
    }
  ];


  const toggleFAQ = (index) => {
    setExpandedFAQ(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <div className="guest-container">
      {/* Section 1: Hero */}
      <section className="esign-section">
        <h1 className="esign-title">Are eSignatures legal?</h1>
        <h2 className="esign-subtitle">Short answer: YES!</h2>
        <p className="esign-description">For the long answer, keep reading.</p>

        <div className="divider-line"></div>

        <div className="quote-card">
          <p className="quote-text">
            "[A contract or signature] may not be denied legal effect, validity, or enforceability solely because it is in electronic form"
          </p>
          <p className="quote-citation">ESIGN Act – USA, June 30, 2000</p>
        </div>
      </section>

      {/* Section 2: Legal Explanation */}
      <section className="explanation-section">
        <div className="content-wrapper">
          <p className="explanation-text">
            The Electronic Signatures in Global and National Commerce Act (ESIGN) is an American law that authorizes the use of electronic records and electronic signatures.
          </p>
          <p className="explanation-text">
            This is a single example of legislation that avails the use and legal effect of electronic signatures for business, across the world.
          </p>
          <p className="explanation-text">
            Each country has its own laws regarding eSignatures but most of them consider electronic signatures and records as legally valid as “wet ink” signatures on paper.
          </p>
          <p className="explanation-text">
            Signaturely complies with all international laws and guidelines that govern electronic signatures.
          </p>
        </div>
      </section>

      {/* <InteractiveEsignMap /> */}

      {/* Section 3: Country Selector */}
      <section className="country-section">
        <h2 className="country-section-title">Are eSignatures legal in my country?</h2>

        <div className="country-grid">
          <div className="country-selector-column">
            <label className="selector-label">Where do you do business?</label>
            <select
              className="country-dropdown"
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>

            <div className="results-grid">
              <div className="result-item">
                <p className="result-label">Holds in court?</p>
                <p className={`result-value ${countryData[selectedCountry].holdsInCourt ? 'yes' : 'no'}`}>
                  {countryData[selectedCountry].holdsInCourt ? 'YES' : 'NO'}
                </p>
              </div>
              <div className="result-item">
                <p className="result-label">Can it be used for Business?</p>
                <p className={`result-value ${countryData[selectedCountry].business ? 'yes' : 'no'}`}>
                  {countryData[selectedCountry].business ? 'YES' : 'NO'}
                </p>
              </div>
            </div>
          </div>

          <div className="map-column">
            <div className="map-card">
              <img src={countryImage} alt={selectedCountry} />
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Countries List */}
      <section className="countries-list-section">
        <h2 className="country-section-title">List of countries where eSignatures are legal</h2>
        <p className="countries-subtitle">
          An eSignature provides the same legal standing as a handwritten signature <strong className="highlight-text">as long as it adheres to the requirements of the specific regulation it was created under.</strong>
        </p>

        <div className="countries-grid">
          {legalCountries.map((item, index) => (
            <div key={index} className="country-card">
              <div className="badge-container">
                <div className="seal-badge">
                  <svg className="seal-circle" viewBox="0 0 200 200">
                    <circle cx="100" cy="100" r="90" fill="none" stroke="#94a3b8" strokeWidth="2" strokeDasharray="3 6" />
                    <path d="M 100 20 A 80 80 0 0 1 100 180" fill="none" stroke="#475569" strokeWidth="1.5" id="topArc" />
                    <path d="M 100 180 A 80 80 0 0 1 100 20" fill="none" stroke="#475569" strokeWidth="1.5" id="bottomArc" />
                    <text fontSize="9" fill="#64748b" fontWeight="700" letterSpacing="2">
                      <textPath href="#topArc" startOffset="50%" textAnchor="middle">
                        ESIGNATURE APPROVED
                      </textPath>
                    </text>
                    <text fontSize="9" fill="#64748b" fontWeight="700" letterSpacing="2">
                      <textPath href="#bottomArc" startOffset="50%" textAnchor="middle">
                        ESIGNATURE APPROVED
                      </textPath>
                    </text>
                  </svg>
                  <div className="ribbon-wrapper">
                    <div className="ribbon-left"></div>
                    <div className="country-ribbon">
                      <img
                        src={`/images/flags/${item.code}.png`}
                        alt={`${item.title} flag`}
                        className="flag-image"
                        width="24"
                        height="16"
                      />
                      <span className="country-name">{item.title}</span>
                    </div>
                    <div className="ribbon-right"></div>
                  </div>

                </div>
              </div>
              <div className="esign-card-content">
                <h3 className="esign-card-title">{item.title}</h3>
                <p className="esign-card-description">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section 5: FAQ */}
      <section className="faq-section">
        <p className="faq-label">FREQUENTLY ASKED QUESTIONS</p>
        <h2 className="country-section-title">Common questions around eSignatures</h2>

        <div className="faq-container">
          {faqs.map((faq, index) => (
            <div key={index} className={`faq-item ${expandedFAQ.includes(index) ? 'expanded' : ''}`}>
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
              >
                <span className="question-text">{faq.question}</span>
                <span className="faq-icon">{expandedFAQ.includes(index) ? '−' : '+'}</span>
              </button>
              {expandedFAQ.includes(index) && (
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                  {index === 0 && (
                    <a href="#" className="learn-more-link">Learn more about eSignatures here.</a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ElectronicSignaturePage;